import React from 'react';
import { Field } from 'formik';

import {
    exhibitionOptions,
    groupingByOptions,
    inspectionRequestStatusExhibitionOptions,
    inspectionResultsExhibitionOptions,
    utilizationOptions,
} from './inspection-report-options';

import CompanyListHelper from '../../helpers/list-helpers';
import CompanyFormatHelper from '../../helpers/format-helpers';

import { useStipReport } from '../../hooks/use-stip-report';
import { useFetchCities } from '../../hooks/fetch/use-fetch-cities';
import { useFetchCompanies } from '../../hooks/fetch/use-fetch-companies';
import { useFetchSubsystems } from '../../hooks/fetch/use-fetch-subsystems';
import { useFetchPersonTypes } from '../../hooks/fetch/use-fetch-person-types';
import { useFetchCompanyTypes } from '../../hooks/fetch/use-fetch-company-types';
import { useFetchVehicleTypes } from '../../hooks/fetch/use-fetch-vehicle-types';

import Loading from '../../../../layouts/Loading';
import CustomMaskField from '../../components/temp/CustomMaskField';
import { GridItem } from '../../components/GridItem';
import { ReportSelect } from '../../components/ReportSelect';
import { ReportDatePicker } from '../../components/ReportDatePicker';
import { ReportMultiSelect } from '../../components/ReportMultiSelect';
import { ReportTogglerGroup } from '../../components/ReportTogglerGroup';
import { ReportSectionTitle } from '../../components/ReportSectionTitle';
import { ReportMultiAutocomplete } from '../../components/ReportMultiAutocomplete';
import { ReportPlateSelection } from '../../components/ReportPlateSelection';
import { useFetchPlates } from '../../hooks/fetch/use-fetch-plates';

const InspectionReport = () => {
    const { handleToggleChange, handleSelectChange } = useStipReport();

    const { loading: loadingPersonTypes, personTypes } = useFetchPersonTypes();
    const { loading: loadingCompanyTypes, companyTypes } = useFetchCompanyTypes();
    const { loading: loadingVehicles, vehicleTypes } = useFetchVehicleTypes();
    const { loading: loadingSubsystems, subsystems } = useFetchSubsystems();
    const { loading: loadingCompanies, companies } = useFetchCompanies({ boWithUsers: true });
    const { loading: loadingCities, cities } = useFetchCities({ allUf: true });
    const { loading: loadingPlates, plates } = useFetchPlates();

    const loading =
        loadingPersonTypes ||
        loadingCompanyTypes ||
        loadingVehicles ||
        loadingSubsystems ||
        loadingCompanies ||
        loadingPlates ||
        loadingCities;

    if (loading) {
        return <Loading />;
    }

    return (
        <GridItem container spacing={3}>
            <GridItem>
                <ReportSectionTitle>Configurar exibição:</ReportSectionTitle>
            </GridItem>
            <GridItem>
                <ReportTogglerGroup options={exhibitionOptions} onToggle={handleToggleChange} />
            </GridItem>

            <GridItem>
                <ReportSectionTitle divider>Opções de filtro:</ReportSectionTitle>
            </GridItem>
            <GridItem>
                <ReportSectionTitle>Por data de realização da vistoria:</ReportSectionTitle>
            </GridItem>
            <GridItem sm={3}>
                <ReportDatePicker label="Início" name="dtVistoria.dtInicio" />
            </GridItem>
            <GridItem sm={3}>
                <ReportDatePicker label="Fim" name="dtVistoria.dtFim" />
            </GridItem>
            <GridItem>
                <ReportSectionTitle>Por data de emissão dos certificados:</ReportSectionTitle>
            </GridItem>
            <GridItem sm={3}>
                <ReportDatePicker label="Início" name="dtEmissao.dtInicio" />
            </GridItem>
            <GridItem sm={3}>
                <ReportDatePicker label="Fim" name="dtEmissao.dtFim" />
            </GridItem>
            <GridItem>
                <ReportSectionTitle>Por data de vencimento dos certificados:</ReportSectionTitle>
            </GridItem>
            <GridItem sm={3}>
                <ReportDatePicker label="Início" name="dtVencimento.dtInicio" />
            </GridItem>
            <GridItem sm={3}>
                <ReportDatePicker label="Fim" name="dtVencimento.dtFim" />
            </GridItem>

            <GridItem>
                <ReportSectionTitle divider>Solicitação:</ReportSectionTitle>
            </GridItem>
            <GridItem sm={3}>
                <Field
                    component={CustomMaskField}
                    name="nuSolicitacao"
                    mask="999999/9999"
                    label="Número da solicitação"
                    variant="outlined"
                />
            </GridItem>

            <GridItem>
                <ReportSectionTitle divider>Selecione o(s) operador(es):</ReportSectionTitle>
            </GridItem>
            <GridItem sm={6}>
                <ReportMultiAutocomplete
                    options={CompanyListHelper.parseDocumentNumber(companies)}
                    onSelect={handleSelectChange}
                    label="Todos os operadores"
                    name="usuarios"
                    valueProperty="deCpfCnpj"
                    labelProperty="deRazaoSocial"
                    getOptionLabel={CompanyFormatHelper.getLabelDeRazaoSocialOrNmCompleto}
                    limitTags={1}
                />
            </GridItem>
            <GridItem>
                <ReportSectionTitle>Selecione o(s) municípios(s):</ReportSectionTitle>
            </GridItem>
            <GridItem sm={6}>
                <ReportMultiAutocomplete
                    options={cities}
                    onSelect={handleSelectChange}
                    label="Todos os municípios"
                    name="municipios"
                    valueProperty="cdMunicipio"
                    labelProperty="nmMunicipio"
                    getOptionLabel={(option) => (!option ? '' : option.nmMunicipio + ' - ' + option.uf?.sgUf)}
                />
            </GridItem>

            <GridItem>
                <ReportSectionTitle divider />
            </GridItem>
            <GridItem sm={6}>
                <ReportSectionTitle gutterBottom>Tipos de operador PJ:</ReportSectionTitle>
                <ReportMultiSelect
                    options={companyTypes}
                    onSelect={handleSelectChange}
                    label="Selecione tipos de operador PJ"
                    name="tiposEmpresa"
                    valueProperty="cdTipoEmpresa"
                    labelProperty="deTipoEmpresa"
                />
            </GridItem>
            <GridItem sm={6}>
                <ReportSectionTitle gutterBottom>Tipos de operador PF:</ReportSectionTitle>
                <ReportMultiSelect
                    options={personTypes}
                    onSelect={handleSelectChange}
                    label="Selecione tipos de operador PF"
                    name="tiposPessoaFisica"
                    valueProperty="cdTipoPessoaFisica"
                    labelProperty="deTipoPessoaFisica"
                />
            </GridItem>

            <GridItem>
                <ReportSectionTitle divider />
            </GridItem>
            <GridItem sm={6}>
                <ReportSectionTitle gutterBottom>Tipos de veículos:</ReportSectionTitle>
                <ReportMultiSelect
                    options={vehicleTypes}
                    onSelect={handleSelectChange}
                    label="Selecione tipos de veículos"
                    name="tiposVeiculo"
                    valueProperty="cdTipoVeiculo"
                    labelProperty="deTipoVeiculo"
                />
            </GridItem>
            <GridItem sm={6}>
                <ReportSectionTitle gutterBottom>Subsistema:</ReportSectionTitle>
                <ReportMultiSelect
                    options={subsystems}
                    onSelect={handleSelectChange}
                    label="Selecione o subsistema"
                    name="subSistemas"
                    valueProperty="cdSubSistema"
                    labelProperty="deSubSistema"
                />
            </GridItem>
            <GridItem sm={6}>
                <ReportSectionTitle gutterBottom>Utilização:</ReportSectionTitle>
                <ReportSelect
                    options={utilizationOptions}
                    name="deUtilizacao"
                    label="Selecione a utilização"
                    onSelect={handleSelectChange}
                />
            </GridItem>
            <GridItem>
                <ReportTogglerGroup
                    options={[{ label: 'Veículo acessível', name: 'boVeiculoAcessivel' }]}
                    onToggle={handleToggleChange}
                />
            </GridItem>
            <GridItem sm={6}>
                <ReportPlateSelection fieldName="placas" plates={plates} loading={loadingPlates} />
            </GridItem>

            <GridItem>
                <ReportSectionTitle divider />
            </GridItem>
            <GridItem sm={6}>
                <ReportSectionTitle gutterBottom>Exibir resultados da vistoria:</ReportSectionTitle>
                <ReportTogglerGroup options={inspectionResultsExhibitionOptions} onToggle={handleToggleChange} />
            </GridItem>
            <GridItem sm={6}>
                <ReportSectionTitle gutterBottom>Situação da solicitação de vistorias:</ReportSectionTitle>
                <ReportTogglerGroup options={inspectionRequestStatusExhibitionOptions} onToggle={handleToggleChange} />
            </GridItem>

            <GridItem>
                <ReportSectionTitle divider>Opções de agrupamento:</ReportSectionTitle>
            </GridItem>
            <GridItem sm={6}>
                <ReportSelect options={groupingByOptions} name="agrupamento" onSelect={handleSelectChange} />
            </GridItem>

            <GridItem>
                <ReportSectionTitle divider>Outras opções:</ReportSectionTitle>
            </GridItem>
            <GridItem sm={6}>
                <ReportTogglerGroup
                    options={[{ label: 'Exibir resumo', name: 'boExibirResumo' }]}
                    onToggle={handleToggleChange}
                />
            </GridItem>
        </GridItem>
    );
};

export default InspectionReport;
